<template>
  <div>
    <v-dialog id="signatureProfileModal" v-model="visible" persistent max-width="90vw" min-width="1280px" @input="backToParent($event)">
      <v-card id="create_signature_profile_root" class="rounded-lg">
        <v-card-title>
          {{ updateMode ? $t('signatureProfiles.title.update') : $t('signatureProfiles.title.create') }}
          <v-spacer></v-spacer>
        </v-card-title>

        <v-form ref="signatureProfileForm" v-model="signatureProfileFormValid" class="pa-6">
          <v-row justify="center" align="center" class="pb-3">
            <v-col>
              <v-text-field
                id="profile_name"
                v-model="signatureProfile.name"
                :label="$t('signatureProfiles.content.name') + `*`"
                :counter="maxCharacters"
                :rules="[rules.required, rules.minMax]"
              ></v-text-field>
            </v-col>

            <v-col>
              <!-- signingCredential -->
              <v-select
                v-if="credentialSelectionEnabled || credentialIsDead "
                id="select_credential"
                v-model="selectedCredential"
                :items="credentialsWithCentralKeyToo"
                :item-text="getCredentialItemText"
                :return-object="true"
                :label="$t('signatureProfiles.content.selectSigningCredential') + `*`"
                :rules="[rules.required]"
              ></v-select>
            </v-col>
          </v-row>

          <v-row justify="center" align="center" class="pb-3">
            <v-col>
              <template v-if="sealEnabled">
                <p v-show=" activatedCentralKeyAndNoCredential && !credentialIsDead" id="warning_activatedCentralKeyAndNoCredential" class="BlueWarning">{{ $t('documentSignatureCustomization.warning.activatedCentralKeyAndNoCredential') }}</p>
                <p v-show=" noActivatedCentralKeyAndNoCredential" id="warning_noActivatedCentralKeyAndNoCredential" class="BlueWarning">{{ $t('documentSignatureCustomization.warning.noActivatedCentralKeyAndNoCredential') }}</p>
              </template>
              <p v-show="credentialIsDead" id="warning_credentialIsDead" class="BlueWarning">{{ $t('documentSignatureCustomization.warning.credentialIsDead') }}</p>
            </v-col>
          </v-row>

          <!-- signatureImageParameters -->
          <drag-drop-upload
            id="upload_image"
            single-image
            :current-imageurl="signatureProfile.sourceImageDataUrl"
            :description="$t('dragDrop.imgDescription')"
            max-width="100%"
            :valid-formats="['image/png','image/jpeg']"
            given-height="200px"
            :add-signature-drawer-button="true"
            @uploaded="handleDragDrop"
            @onSignatureDrawerClicked="openSignatureModal"
          ></drag-drop-upload>

          <v-row justify="space-around" align="center" class="pt-3">
            <v-col>
              <v-text-field
                id="image_text"
                v-model="signatureImagetextParams.text"
                :label="$t('signatureProfiles.content.signatureImageText')"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row justify="space-around" align="center" class="pt-1 mt-1">
            <v-col cols="6" align-self="start">
              <v-container class="ml-0 pl-0">
                <v-row no-gutters justify="start" align="start">
                  <v-col align-self="start" cols="6">
                    <v-slider v-if="showFontSizeController" v-model="textToImageRatio" class="ma-0" thumb-label="always" min="1" max="100" step="1" :disabled="!signatureImagetextParams.text" :label="$t('signatureProfiles.content.signatureImageTextRatio')"></v-slider>
                  </v-col>
                  <!-- <v-col align-self="start" cols="2">
                    <v-btn v-if="showFontSizeController" @click="setDefaultTextToImageRatio">
                      <v-icon>mdi-content-save-outline</v-icon>
                    </v-btn>
                  </v-col> -->
                </v-row>
                <v-row no-gutters justify="start" align="start">
                  <v-col align-self="start" class="ml-0 pl-0">
                    <v-select
                      v-if="signatureImagetextParams?.text"
                      id="font_select"
                      v-model="selectedFont"
                      :items="allowedFonts"
                      :label="$t('signatureProfiles.content.selectFont')"
                      item-value="family"
                    >
                      <template #selection="{ item }">
                        <span :style="{ fontFamily: 'sans-serif' }">{{ item.name }}</span>
                        <span :style="{ fontFamily: item.family, marginLeft: '8px' }">{{ fontPreviewText }}</span>
                      </template>

                      <template #item="{ item, attrs }">
                        <div v-bind="attrs" class="d-flex align-center">
                          <span :style="{ fontFamily: 'sans-serif' }">{{ item.name }}</span>
                          <span :style="{ fontFamily: item.family, marginLeft: '8px' }">{{ fontPreviewText }}</span>
                        </div>
                      </template>
                    </v-select>
                  </v-col>
                </v-row>
                <v-row v-if="showFontSizeController" no-gutters justify="start" align="start" align-content="center">
                  <v-col align-self="start" cols="3">
                    <p :disabled="!signatureImagetextParams.text">{{ $t('signatureProfiles.content.textColorLabel') }} {{ signatureTextColor }}</p>
                  </v-col>
                  <v-col cols="3">
                    <v-menu v-model="signatureTextColormenuOpen" :close-on-content-click="false" offset-y>
                      <template #activator="{ on }">
                        <v-btn class="white--text modalButton" :disabled="!signatureImagetextParams.text" v-on="on">
                          {{ $t('signatureProfiles.content.chooseTextColor') }}
                        </v-btn>
                      </template>
                      <v-card>
                        <v-color-picker v-model="signatureTextColor" value="hex"></v-color-picker>
                      </v-card>
                    </v-menu>
                  </v-col>
                </v-row>
                <!-- <v-row no-gutters>
                  <v-col>
                    <v-checkbox v-model="textBackgroundColorIsTransparent" :disabled="!signatureImagetextParams.text" :label="$t('signatureProfiles.content.textBackgroundColorIsTransparent')"></v-checkbox>
                  </v-col>
                </v-row>
                <v-row v-if="!textBackgroundColorIsTransparent" no-gutters justify="space-around" align="baseline">
                  <v-col v-if="!textBackgroundColorIsTransparent" align-self="baseline">
                    <p>{{ $t('signatureProfiles.content.textBackgroundColorLabel') }} {{ signatureTextBackgroundColor }}</p>
                  </v-col>
                  <v-col>
                    <v-menu v-model="signatureTextBackgroundColormenuOpen" :close-on-content-click="false" offset-y>
                      <template v-slot:activator="{ on }">
                        <v-btn class="white--text modalButton" :disabled="!signatureImagetextParams.text" v-on="on">
                          {{ $t('signatureProfiles.content.chooseTextBackgroundColor') }}
                        </v-btn>
                      </template>
                      <v-card>
                        <v-color-picker v-model="signatureTextBackgroundColor" value="hex"></v-color-picker>
                      </v-card>
                    </v-menu>
                  </v-col>
                </v-row> -->
              </v-container>
            </v-col>
            <v-col cols="6">
              <div>
                <canvas id="preview" ref="canvas" width="600" height="200"></canvas>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <signature-drawer ref="signatureDrawer" v-model="showSignatureModal" @saved="handleCustomSignature"></signature-drawer>
          </v-row>
        </v-form>

        <v-card-actions>
          <v-btn
            id="cancel_profile"
            outlined
            class="my-4"
            color="primary"
            @click="backToParent(false)"
          >
            <v-icon>mdi-close-circle-outline</v-icon>
            {{ $t('main.cancel') }}
          </v-btn>
          <v-btn
            id="create_profile"
            color="primary"
            :disabled="saveButtonDisabled"
            :loading="isUploading"
            @click="createOrUpdateSignatureProfile"
          >
            <v-icon>{{ updateMode ? "mdi-content-save-outline" : "mdi-plus-circle-outline" }}</v-icon>
            <template #append>
              <span v-if="saveButtonDisabled">DISABLED</span>
            </template>
            {{ updateMode ? $t('main.update') : $t('main.create') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-row justify="center">
      <v-dialog v-model="noCredentialWarningVisible" persistent width="auto" max-width="700">
        <v-card id="no_credential_warning" class="py-6 px-3">
          <v-card-title class="text-h6 red--text">
            {{ $t('signatureProfiles.error.noActivatedCredential.title') }}
          </v-card-title>
          <v-card-text>
            <div>
              {{ $t('signatureProfiles.error.noActivatedCredential.description') }}
            </div>
            <v-btn
              id="no_credential_warning_ok"
              style="text-align: center;"
              color="primary"
              to="/credentials"
              class="mt-10 mb-0"
            >
              <v-icon class="mr-1">mdi-folder-open-outline</v-icon>
              {{ $t('signatureProfiles.error.noActivatedCredential.button') }}
            </v-btn>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-row>
  </div>
</template>

<script>
import DragDropUpload from '@/components/DragDropUpload.vue'
import SignatureDrawer from '@/components/SignatureDrawer.vue'
import credentialRefs from '@/services/api/credentialRefs'
import evro from '@/services/api/evro'
import rssp from '@/services/api/rsspUtil.js'
import seal from '@/services/api/seal.js'
import signatures from '@/services/api/signatures'
import users from '@/services/api/users'
import { fabric } from 'fabric'
import FontFaceObserver from 'fontfaceobserver'
import debounce from 'lodash.debounce'
import { showToastError, showToastSuccess } from '../../../event-bus'
import alfaRefs from '../../../services/api/alfaRefs'
import signatureProfiles from '../../../services/api/signatureProfiles'
import CommonError from '../../CommonError'

export default {
  name: 'CreateOrUpdateSignatureProfile',
  components: {
    SignatureDrawer, DragDropUpload
  },

  data() {
    return {
      visible: true,
      user: null,
      evroEnabled: this.$store.state.remoteConfig.evroEnabled ? this.$store.state.remoteConfig.evroEnabled : false,
      signatureProfileFormValid: false,
      noCredentialWarningVisible: false,
      maxCharacters: 255,
      activatedCredentials: [],
      credentialRefs: [],
      credentialsEvro: [],
      credentialsAlfa: [],
      credentialsWithCentralKeyToo: [],
      signatureImageUrl: '',
      signatureImageFile: {},
      signatureImage: null,
      showSignatureModal: false,
      setSignatureImagetextParams: false,
      signatureImagetextParams: {},
      // signatureImageTextPositionList: ['TOP', 'BOTTOM', 'RIGHT', 'LEFT'],
      textToImageRatio: 24,
      defaultTextToImageRatio: 24,
      signatureProfile: {},
      selectedCredential: undefined,
      rules: {
        fileRequired: (v) => !!this.signatureImageUrl,
        required: (v) => !!v || !isNaN(parseFloat(v)) || this.$t('validation.form.requiredField'),
        minMax: (v) => (v && v.length <= this.maxCharacters) || this.$t('validation.form.mustBeLessThan', { max: this.maxCharacters })
      },
      isUploading: false,
      sealStatus: {
        enabled: false,
        secureMode: false,
        activated: false
      },
      credentialIsDead: false,
      biggestSignatureImageSize: 1024 * 1024,
      defaultCanvasWidth: 600,
      defaultTextHeight: 20,
      defaultCanvasHeight: 200,

      rendering: false,
      underSavingTheProfile: false,
      paddingPercent: 0.10,
      userVerificationImage: null,
      haveImportedcontent: false,
      signatureTextColormenuOpen: false,
      signatureTextBackgroundColormenuOpen: false,
      img: null,
      imageNativeWidth: 0,
      imageNativeHeight: 0,
      updatingObjects: false,
      fcanvas: null,
      signatureFabricText: null,
      signatureImg: null,
      anottationControls: { mtr: false, mr: false, ml: false, mt: false, mb: false },
      signatureDragHandleColor: '#FBB615',
      // signatureTextFont: 'PT Serif',
      signatureTextFont: 'Roboto',
      signatureTextColor: '#000000',
      textBackgroundColorIsTransparent: true,
      signatureTextBackgroundColor: 'transparent',
      signatureTextAlign: 'center',
      // imageDataFormat: { format: 'image/png' },
      imageDataFormat: 'image/png',
      signatureVerificationImg: null,
      backgroundImg: null,
      backgroundColor: '#FFFFFF',
      fabricBackgroundImg: null,
      drawBackground: true,

      signerTextVisible: false,
      signerImageVisible: false,
      aspectRatio: true,
      leftBlockWidth: 300,
      imageWidth: 0,
      imageHeight: 0,
      scaledWidth: 0,
      scaledHeight: 0,
      initialImageWidth: 0,
      initialImageHeight: 0,
      fontPreviewText: this.$t('signatureProfiles.content.fontPreviewText'),
      selectedFont: 'Kolker Brush',
      allowedFonts: [

        { name: 'Lato', family: 'Lato' },
        { name: 'Lora', family: 'Lora' },
        { name: 'Merriweather', family: 'Merriweather' },
        { name: 'Montserrat', family: 'Montserrat' },
        { name: 'Open Sans', family: 'Open Sans' },
        { name: 'Oswald', family: 'Oswald' },
        { name: 'Playfair Display', family: 'Playfair Display' },
        { name: 'PT Sans', family: 'PT Sans' },
        { name: 'Raleway', family: 'Raleway' },
        { name: 'Roboto', family: 'Roboto' },
        { name: 'Source Sans 3', family: 'Source Sans 3' },
        { name: 'Caveat', family: 'Caveat' },
        { name: 'Dancing Script', family: 'Dancing Script' },
        { name: 'Kolker Brush', family: 'Kolker Brush' },
        { name: 'Playwrite HR', family: 'Playwrite HR' },
        { name: 'Sacramento', family: 'Sacramento' },
        { name: 'WindSong', family: 'WindSong' }
      ]
    }
  },

  computed: {
    updateMode() {
      return this.$route.params.signatureProfileId != null
    },
    profileId() {
      return this.$route.params.signatureProfileId
    },
    credentialSelectionEnabled() {
      return (this.credentialsWithCentralKeyToo.length > 0)
    },
    sealEnabled() {
      return this.sealStatus.enabled
      // return true
    },
    sealActivated() {
      return this.sealStatus.enabled && this.sealStatus.activated
      // return false
    },
    noActivatedCentralKeyAndNoCredential() {
      return (!this.sealActivated && this.activatedCredentials.length === 0)
    },
    noActivatedCredentials() {
      return this.activatedCredentials.length === 0
    },
    noImportedCredentials() {
      return (this.credentialRefs.length === 0) && (this.credentialsEvro.length === 0)
    },
    activatedCentralKeyAndNoCredential() {
      return (this.sealActivated && this.noActivatedCredentials && this.noImportedCredentials)
    },
    showFontSizeController() {
      return this.signerTextVisible && this.signerImageVisible
    },
    saveButtonDisabled() {
      return this.updatingObjects || !this.checkFormValid()
    }
  },

  watch: {
    'signatureImagetextParams.text': debounce(function(newText, oldText) {
      if (this.underSavingTheProfile || this.updatingObjects) {
        return
      }
      if (this.signatureFabricText === null) {
        this.initPreview(false)
      } else {
        if ((newText.length === 0) || (newText.length === 0)) {
          this.signatureFabricText = null
          this.initPreview(false)
        } else {
          this.signatureFabricText.set('text', newText)
          this.initPreview(false)
        }
      }
    }, 1000),

    textToImageRatio(val) {
      if (this.updatingObjects) {
        return
      }

      this.initPreview(false)
    },
    selectedFont(val) {
      if (this.underSavingTheProfile || this.updatingObjects) {
        return
      }
      this.signatureFabricText = null

      this.$nextTick(() => {
        this.initPreview(false)
      })
    },
    signatureTextColor(val) {
      if (this.underSavingTheProfile || this.updatingObjects) {
        return
      }
      if (this.signatureFabricText === null) {
        this.initPreview(false)
      } else {
        if (val.length === 0) {
          this.initPreview(false)
        } else {
          this.signatureFabricText.set('fill', val)
          this.fcanvas.renderAll()
        }
      }
    },
    signatureTextBackgroundColor(val) {
      if (this.underSavingTheProfile || this.updatingObjects) {
        return
      }
      if (this.signatureFabricText === null) {
        this.initPreview(false)
      } else {
        this.signatureFabricText = null
        this.initPreview(false)
        // this.signatureFabricText.set('backgroundColor', this.signatureTextBackgroundColor)
        // this.fcanvas.renderAll()
      }
    },
    textBackgroundColorIsTransparent(val) {
      if (this.signatureFabricText) {
        if (val) {
          this.signatureTextBackgroundColor = 'transparent'
          this.fcanvas.renderAll()
        } else {
          const color = new fabric.Color(this.signatureTextBackgroundColor)
          const rgb = color.getSource()
          rgb[3] = 1 // set alpha to 1 to remove transparency
          var hex = '#' + ((1 << 24) + (rgb[0] << 16) + (rgb[1] << 8) + rgb[2]).toString(16).slice(1)
          this.signatureTextBackgroundColor = hex
          // this.fcanvas.renderAll()
        }
      }
    }
  },
  beforeMount() {

  },
  async mounted() {
    window.fabric.Object.prototype.objectCaching = false

    const font = new FontFaceObserver(this.signatureTextFont)

    font.load().then(function() {
    })

    this.sealStatus = await seal.getSealStatus()

    this.user = await users.getMyUser()

    if (this.sealEnabled) {
      this.credentialsWithCentralKeyToo.unshift({ id: -1, description: this.$t('documentSignatureCustomization.content.signature.centralKey') })
    }
    await this.listMyActivatedCredentials()
    await this.listImportedCredentials()

    this.credentialsWithCentralKeyToo = [...this.credentialsWithCentralKeyToo, ...this.activatedCredentials, ...this.credentialRefs, ...this.credentialsEvro, ...this.credentialsAlfa]

    if (this.credentialsWithCentralKeyToo.length === 0) {
      this.noCredentialWarningVisible = true
    }

    if (this.sealEnabled && this.activatedCredentials.length === 0 && this.credentialRefs.length === 0 && this.credentialsEvro.length === 0 && this.credentialsAlfa.length === 0) {
      this.signatureProfile.useSeal = true
      this.selectedCredential = this.credentialsWithCentralKeyToo[0]
    }

    if (this.updateMode) {
      await this.getSignatureProfile()
    }

    this.loadGoogleFonts()

    this.userVerificationImage = await signatures.getVisibleSignatureUserVerificationImage(this.signatureProfile.id)
    await this.getVerificationImageDimensions(this.userVerificationImage.data.imageDataUrl)

    const resp = await signatures.getVisisigAdminProps()
    this.backgroundColor = resp.data.bgColor ? resp.data.bgColor : '#FFFFFF'
    this.backgroundImageUrl = resp.data.bgImageDataUrl ? resp.data.bgImageDataUrl : null

    if (this.backgroundImageUrl) {
      this.backgroundImg = new Image()
      this.backgroundImg.src = this.backgroundImageUrl
      await this.backgroundImg.decode()
      this.fabricBackgroundImg = new fabric.Image(this.backgroundImg)
    }
    this.initPreview()
  },
  destroyed() {
    this.signatureProfile = {}
  },
  methods: {
    setFontSize(value) {
      if (this.signatureFabricText) {
        this.signatureFabricText.set('fontSize', value)
        this.fcanvas.renderAll()
      }
    },
    setTextBackgroundColor() {
      if (this.signatureFabricText) {
        this.signatureTextBackgroundColor = 'transparent'
      }
    },
    async doTheRendering(reloadImg) {
      /* const start = performance.now()
      while (performance.now() - start < 5000) {} // block 1 second
      return */
      if (this.fcanvas) {
        this.fcanvas.remove(...this.fcanvas.getObjects())
        this.fcanvas.clear()
        this.fcanvas.dispose()
        this.fcanvas = null
        this.signatureFabricText = null
        this.signatureImg = null
      }

      if (!this.fcanvas) {
        this.fcanvas = new fabric.Canvas('preview', {
          selection: false
        })
      }
      this.fcanvas.setWidth(this.defaultCanvasWidth)
      this.fcanvas.setHeight(this.defaultCanvasHeight)

      if (this.drawBackground) {
        if (!this.backgroundImageUrl) {
          this.fcanvas.backgroundColor = this.backgroundColor
        } else {
          this.fcanvas.backgroundColor = 'transparent'
        }
        if (this.fabricBackgroundImg) {
          this.fcanvas.setBackgroundImage(this.fabricBackgroundImg, this.fcanvas.renderAll.bind(this.fcanvas), {
            scaleX: this.fcanvas.width / this.backgroundImg.width,
            scaleY: this.fcanvas.height / this.backgroundImg.height
          })
          this.fcanvas.renderAll()
        }
      }

      this.leftBlockWidth = this.fcanvas.width / 2
      // Calculate the padding (10% of the left half width and canvas height)
      this.paddingX = this.leftBlockWidth * this.paddingPercent
      this.paddingY = this.fcanvas.height * this.paddingPercent

      this.paddedWidth = this.leftBlockWidth - (2 * this.paddingX)
      this.paddedHeight = this.fcanvas.height - (2 * this.paddingY)

      if (this.signatureImageUrl) {
        this.$set(this.signatureProfile, 'sourceImageDataUrl', this.signatureImageUrl)
      } else {
        this.$delete(this.signatureProfile, 'sourceImageDataUrl')
      }

      if (this.signatureImagetextParams) {
        this.$set(this.signatureProfile, 'textParams', Object.assign({}, this.signatureImagetextParams))
      } else {
        if (this.signatureProfile.sourceImageDataUrl) {
          this.$delete(this.signatureProfile, 'textParams')
        }
      }

      if ((this.signatureProfile) && (this.signatureProfile.textParams) && (this.signatureProfile.textParams.text) && (this.signatureProfile.textParams.text.length > 0)) {
        this.signerTextVisible = true
      } else {
        this.signerTextVisible = false
      }

      if (!reloadImg && this.img == null) {
        reloadImg = true
      }
      if (reloadImg) {
        if (this.signatureProfile.sourceImageDataUrl) {
          this.signerImageVisible = true
          await this.getImageDimensions(this.signatureProfile.sourceImageDataUrl)
          this.aspectRatio = true
        } else {
          this.aspectRatio = false
          this.signerImageVisible = false
          this.imageWidth = this.defaultCanvasWidth
          this.imageHeight = this.defaultTextHeight
        }
      } else {
        if (this.signatureProfile.sourceImageDataUrl) {
          this.signerImageVisible = true
        }
        if (this.signatureProfile.sourceImageDataUrl) {
          this.calculateImageAspectRatioFit(this.img)
        }
      }

      if (this.signerTextVisible && !this.signerImageVisible) {
        this.calculateImageAspectRatioFit(null)
      }
    },

    initPreview(reloadImg = true) {
      this.updatingObjects = true
      this.rendering = true

      return new Promise((resolve) => {
        this.$nextTick(async () => {
          await this.doTheRendering(reloadImg)
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.$nextTick(() => {
                this.updatingObjects = false
                this.rendering = false
                resolve()
              })
            })
          })
        })
      })
    },

    getAutoSizeTextScale() {
      const canvasWidth = this.fcanvas.getWidth()
      const canvasHeight = this.fcanvas.getHeight()
      const textWidth = this.signatureFabricText.getScaledWidth()
      const textHeight = this.signatureFabricText.getScaledHeight()
      const scale = Math.min(canvasWidth / textWidth, canvasHeight / textHeight)
      return scale
    },

    setDefaultTextToImageRatio() {
      this.signatureFabricText.scaleToWidth(this.signatureImg.getScaledWidth())
      this.signatureFabricText.setCoords()
      this.fcanvas.renderAll()

      const newTextHeight = this.signatureFabricText.getScaledHeight()
      const heightRatio = (newTextHeight / this.paddedHeight) * 100

      // Step 3: Update the textToImageRatio
      this.textToImageRatio = heightRatio

      this.fcanvas.renderAll()
    },

    calculateImageAspectRatioFit(img) {
      this.fcanvas.originX = 'left'
      this.fcanvas.originY = 'top'
      this.fcanvas.centeredScaling = false

      if (this.signerImageVisible) {
        this.signatureImg = new fabric.Image(img, {
          left: 0,
          top: 0,
          originX: 'left',
          originY: 'top',
          transparentCorners: false,
          borderColor: this.signatureDragHandleColor,
          cornerColor: this.signatureDragHandleColor,
          selectable: false, // disable selection
          hasControls: false, // disable control handles
          hasBorders: false, // disable borders
          hoverCursor: 'default',
          centeredScaling: false
        })
      }

      // this.signatureImg.setControlsVisibility(this.anottationControls)

      let simageHeight = 0
      if (this.signerImageVisible) {
        simageHeight = this.signatureImg.getScaledHeight()
      }

      if (this.signerTextVisible) {
        this.addSignatureText(0, simageHeight)
      }

      this.signatureVerificationImg = new fabric.Image(this.verificationImg, {
        left: this.leftBlockWidth,
        top: 0,
        originY: 'top',
        originX: 'left',
        transparentCorners: false,
        borderColor: this.signatureDragHandleColor,
        cornerColor: this.signatureDragHandleColor,
        selectable: false, // disable selection
        hasControls: false, // disable control handles
        hasBorders: false, // disable borders
        hoverCursor: 'default',
        centeredScaling: false
      })
      this.signatureVerificationImg.scaleToHeight(this.fcanvas.height)

      if (this.signerImageVisible) {
        this.fcanvas.add(this.signatureImg)
      }
      if (this.signerTextVisible) {
        this.fcanvas.add(this.signatureFabricText)
      }
      this.fcanvas.add(this.signatureVerificationImg)

      if (this.signerTextVisible && this.signerImageVisible) {
        this.signatureFabricText.scaleToWidth(this.paddedWidth)
        const maxWidth = this.signatureFabricText.getScaledWidth()
        this.signatureImg.scaleToHeight(this.paddedHeight * (100 - this.textToImageRatio) / 100)

        if (this.signatureImg.getScaledWidth() > maxWidth) {
          this.signatureImg.scaleToWidth(maxWidth)
        }
        const newTextH = this.paddedHeight * this.textToImageRatio / 100
        this.signatureFabricText.scaleToHeight(newTextH)
        if (this.signatureFabricText.getScaledWidth() > maxWidth) {
          this.signatureFabricText.scaleToWidth(maxWidth)
        }
        this.signatureImg.left = this.paddingX + (this.paddedWidth - this.signatureImg.getScaledWidth()) / 2
        this.signatureImg.top = this.paddingY
        this.signatureImg.setCoords()

        const textH = this.signatureFabricText.getScaledHeight()
        this.signatureFabricText.left = this.paddingX + (this.paddedWidth - this.signatureFabricText.getScaledWidth()) / 2
        this.signatureFabricText.top = this.fcanvas.height - textH - this.paddingY
        this.signatureFabricText.setCoords()
      } else
      if (this.signerTextVisible) {
        this.signatureFabricText.scaleToWidth(this.paddedWidth)
        const maxWidth = this.signatureFabricText.getScaledWidth()

        this.signatureFabricText.left = 0
        this.signatureFabricText.top = 0

        this.signatureFabricText.scaleToHeight(this.fcanvas.height)

        this.signatureFabricText.setCoords()

        this.signatureFabricText.scaleToHeight(this.paddedHeight)
        if (this.signatureFabricText.getScaledWidth() > maxWidth) {
          this.signatureFabricText.scaleToWidth(maxWidth)
        }
        // this.signatureFabricText.scaleToWidth(this.paddedWidth)
        // this.signatureFabricText.scaleToHeight(100)
        this.fcanvas.renderAll()

        // Position the object in the center of the padded area
        this.signatureFabricText.left = this.paddingX + (this.paddedWidth - this.signatureFabricText.getScaledWidth()) / 2
        this.signatureFabricText.top = this.paddingY + (this.paddedHeight - this.signatureFabricText.getScaledHeight()) / 2

        this.signatureFabricText.setCoords()
      } else
      if (this.signerImageVisible) {
        this.signatureImg.scaleToWidth(this.paddedWidth)
        if (this.signatureImg.getScaledHeight() > this.paddedHeight) {
          this.signatureImg.scaleToHeight(this.paddedHeight)
        }

        this.signatureImg.setCoords()

        // Position the object in the center of the padded area
        this.signatureImg.left = this.paddingX + (this.paddedWidth - this.signatureImg.getScaledWidth()) / 2
        this.signatureImg.top = this.paddingY + (this.paddedHeight - this.signatureImg.getScaledHeight()) / 2

        this.signatureImg.setCoords()
      }

      this.fcanvas.renderAll()
    },
    async getImageDimensions(imageDataUrl) {
      this.img = new Image()
      this.img.src = imageDataUrl

      await this.img.decode()
      this.imageNativeWidth = this.img.naturalWidth
      this.imageNativeHeight = this.img.naturalHeight

      this.calculateImageAspectRatioFit(this.img)
    },
    async getVerificationImageDimensions(imageDataUrl) {
      this.verificationImg = new Image()
      this.verificationImg.src = imageDataUrl
      await this.verificationImg.decode()
      this.verificationImgNativeWidth = this.verificationImg.naturalWidth
      this.verificationImgNativeHeight = this.verificationImg.naturalHeight
    },
    addSignatureText(textLeft, textTop, w, h) {
      this.signatureFabricText = new fabric.IText(this.signatureProfile.textParams.text, {
        left: textLeft,
        top: textTop,
        originY: 'top',
        originX: 'left',
        transparentCorners: false,
        // fontFamily: this.signatureTextFont,
        fontFamily: this.selectedFont,
        // fontSize: this.textToImageRatio,
        fontSize: 20,
        textAlign: this.signatureTextAlign,
        fill: this.signatureTextColor,
        backgroundColor: this.textBackgroundColorIsTransparent ? 'transparent' : this.signatureTextBackgroundColor,
        /* lockScalingX: true,
        lockScalingY: true, */
        selectable: false, // disable selection
        hasControls: false, // disable control handles
        // hasBorders: false, // disable borders
        hasBorders: true, // disable borders
        lockMovementX: true, // lock horizontal movement
        lockMovementY: true, // lock vertical movement
        // lockUniScaling: false,
        editable: false, // set editable property to false to disable inline editing
        // lockUniScaling: true,
        // borderColor: this.signatureDragHandleColor,
        borderColor: this.signatureTextColor,
        cornerColor: this.signatureDragHandleColor,
        centeredScaling: false,
        objectCaching: false,
        padding: 0,
        strokeWidth: 0,
        hoverCursor: 'default'
      })

      this.signatureFabricText.setControlsVisibility({ mtr: false, mr: false, ml: false })
    },

    checkFormValid() {
      return this.signatureProfileFormValid && ((this.signatureImageUrl && this.signatureImageUrl.length > 0) || (this.signatureImagetextParams.text && this.signatureImagetextParams.text.length > 0))
    },

    getBackendColorObject(colorhex) {
      const fcolor = new fabric.Color(colorhex)
      const rgb = fcolor.getSource()
      return { red: rgb[0], green: rgb[1], blue: rgb[2] }
    },
    getHexColorFromBackendColor(colorObject) {
      return '#' + ((1 << 24) + (colorObject.red << 16) + (colorObject.green << 8) + colorObject.blue).toString(16).slice(1)
    },
    async createOrUpdateSignatureProfile() {
      if (!this.$refs.signatureProfileForm.validate()) {
        return
      }

      this.underSavingTheProfile = true

      if (this.sealActivated && ((!this.selectedCredential) || (this.selectedCredential.id === -1))) {
        this.$set(this.signatureProfile, 'useSeal', true)
        this.$delete(this.signatureProfile, 'credentialId')
        this.$delete(this.signatureProfile, 'credentialRefId')
      } else
      if (this.selectedCredential && this.selectedCredential.provider) {
        this.$delete(this.signatureProfile, 'credentialId')
        this.$delete(this.signatureProfile, 'evroRefId')
        // this.$delete(this.signatureProfile, 'useSeal')
        this.$set(this.signatureProfile, 'useSeal', false)
        this.$set(this.signatureProfile, 'credentialRefId', this.selectedCredential.id)
      } else
      if (this.selectedCredential && this.selectedCredential.isEvro) {
        this.$delete(this.signatureProfile, 'credentialRefId')
        this.$delete(this.signatureProfile, 'credentialId')
        // this.$delete(this.signatureProfile, 'useSeal')
        this.$set(this.signatureProfile, 'useSeal', false)
        this.$set(this.signatureProfile, 'evroRefId', this.selectedCredential.id)
      } else
      if (this.selectedCredential && this.selectedCredential.isAlfa) {
        this.$delete(this.signatureProfile, 'credentialRefId')
        this.$delete(this.signatureProfile, 'credentialId')
        this.$delete(this.signatureProfile, 'useSeal')
        this.$delete(this.signatureProfile, 'evroRefId')
        this.$set(this.signatureProfile, 'alfaRefId', this.selectedCredential.id)
      } else
      if (this.selectedCredential) {
        this.$delete(this.signatureProfile, 'credentialRefId')
        this.$delete(this.signatureProfile, 'evroRefId')
        // this.$delete(this.signatureProfile, 'useSeal')
        this.$set(this.signatureProfile, 'useSeal', false)
        this.$set(this.signatureProfile, 'credentialId', this.selectedCredential.id)
      }

      // signatureImageParameters
      if (this.signatureImageUrl) {
        this.$set(this.signatureProfile, 'sourceImageDataUrl', this.signatureImageUrl)
      } else {
        this.$delete(this.signatureProfile, 'sourceImageDataUrl')
      }

      // signatureImagetextParams
      if (this.signatureImagetextParams) {
        this.$set(this.signatureImagetextParams, 'textColor', this.getBackendColorObject(this.signatureTextColor))

        if (this.textBackgroundColorIsTransparent) {
          this.$delete(this.signatureImagetextParams, 'backgroundColor')
        } else {
          this.$set(this.signatureImagetextParams, 'backgroundColor', this.getBackendColorObject(this.signatureTextBackgroundColor))
        }

        // this.$set(this.signatureImagetextParams, 'textToImageRatio', this.textToImageRatio)
        this.$set(this.signatureImagetextParams, 'size', this.textToImageRatio)

        this.$set(this.signatureProfile, 'textParams', this.signatureImagetextParams)
      } else {
        if (this.signatureProfile.sourceImageDataUrl) {
          this.$delete(this.signatureProfile, 'textParams')
        }
      }

      this.drawBackground = false
      try {
        await this.initPreview(false)
      } finally {
        this.drawBackground = true
      }

      // get correct canvas element with correct devicePixelRatio
      const canvasEl = this.fcanvas.lowerCanvasEl
      const ctx = canvasEl.getContext('2d')
      const cropWidth = canvasEl.width / 2
      const myImageData = ctx.getImageData(0, 0, cropWidth, canvasEl.height)
      const buffer = document.createElement('canvas')
      const bufferCtx = buffer.getContext('2d')
      buffer.width = cropWidth
      buffer.height = canvasEl.height
      bufferCtx.putImageData(myImageData, 0, 0)

      const renderedImageDataUrl = buffer.toDataURL(this.imageDataFormat)
      this.$set(this.signatureProfile, 'renderedImageDataUrl', renderedImageDataUrl)
      // console.log('renderedImageDataUrl', renderedImageDataUrl)

      try {
        this.isUploading = true
        this.updateMode
          ? await signatureProfiles.updateMySignatureProfile(this.profileId, this.signatureProfile)
          : await signatureProfiles.createMySignatureProfile(this.signatureProfile)
        showToastSuccess(this.updateMode ? this.$t('toastMessage.succesfullyUpdated') : this.$t('toastMessage.succesfullyCreated'))
        this.backToParent(false)
        this.$emit('uploaded')
      } catch (error) {
        let errorMsg = this.$t('toastMessage.errorWhileRetrievingData')
        if ((error.response !== undefined) && (error.response.data.code !== undefined)) {
          if (error.response.data.code === CommonError.ALREADY_EXISTS) {
            errorMsg = this.$t('toastMessage.alreadyExist', { 0: this.signatureProfile.name })
          } else {
            errorMsg = this.getErrorMessage(error)
          }
        } else {
          errorMsg = this.getErrorMessage(error)
        }
        showToastError(errorMsg)

        this.backToParent(false)
      } finally {
        this.isUploading = false
        this.underSavingTheProfile = false
      }
    },

    async getSignatureProfile() {
      try {
        this.signatureProfile = await signatureProfiles.getMySignatureProfile(this.profileId)

        await this.setDefaultValuesByGetSignatureProfile()
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    async getCredentialIdFromRef(credentialRefId) {
      const ref = await credentialRefs.getCredentialRef(credentialRefId)
      return ref.credentialId
    },

    findCredentialByEvroRefId(searchId) {
      const matchingCredential = this.credentialsWithCentralKeyToo.find(item => (item.isEvro && parseInt(item.id) === searchId))
      return matchingCredential
    },
    findCredentialByAlfaRefId(searchId) {
      const matchingCredential = this.credentialsWithCentralKeyToo.find(item => (item.isAlfa && parseInt(item.id) === searchId))
      return matchingCredential
    },

    findCredentialByRefId(searchId) {
      const matchingCredential = this.credentialsWithCentralKeyToo.find(item => (item.provider && parseInt(item.id) === searchId))
      return matchingCredential
    },

    findCredentialById(searchId) {
      const matchingCredential = this.credentialsWithCentralKeyToo.find(item => (!item.provider && parseInt(item.id) === searchId))
      return matchingCredential
    },

    async setDefaultValuesByGetSignatureProfile() {
      if (this.signatureProfile.useSeal === true) {
        this.selectedCredential = this.findCredentialById(-1)
      } else
      if (this.signatureProfile.credentialRefId) {
        this.selectedCredential = this.findCredentialByRefId(parseInt(this.signatureProfile.credentialRefId))
      } else
      if (this.signatureProfile.evroRefId) {
        this.selectedCredential = this.findCredentialByEvroRefId(parseInt(this.signatureProfile.evroRefId))
      } else
      if (this.signatureProfile.alfaRefId) {
        this.selectedCredential = this.findCredentialByAlfaRefId(parseInt(this.signatureProfile.alfaRefId))
      } else {
        this.selectedCredential = this.findCredentialById(parseInt(this.signatureProfile.credentialId))

        if (!!this.signatureProfile.credentialId && !(this.activatedCredentials.find(x => x.id === parseInt(this.signatureProfile.credentialId)))) {
          this.credentialIsDead = true
          this.signatureProfile.credentialId = undefined
        }
      }

      // signatureImageParameters
      if (this.signatureProfile.sourceImageDataUrl) {
        this.signatureImageUrl = this.signatureProfile.sourceImageDataUrl
        const splittedSigImgDataUrl = this.signatureProfile.sourceImageDataUrl.split(';')
        if (splittedSigImgDataUrl.length > 2) {
          this.signatureImageFile = new File([splittedSigImgDataUrl[2].split(',')[1]], splittedSigImgDataUrl[1].split('=')[1],
            { type: splittedSigImgDataUrl[0].split(':')[1] })
        } else {
          this.signatureImageFile = new File([splittedSigImgDataUrl[1].split(',')[1]], 'Rendered image',
            { type: splittedSigImgDataUrl[0].split(':')[1] })
        }
      }

      // signatureImagetextParams
      if (this.signatureProfile.textParams) {
        this.signatureImagetextParams = this.signatureProfile.textParams

        if (this.signatureImagetextParams.textColor) {
          this.signatureTextColor = this.getHexColorFromBackendColor(this.signatureImagetextParams.textColor)
        } else {
          this.signatureTextColor = '#000000'
        }
        if (this.signatureImagetextParams.backgroundColor) {
          this.signatureTextBackgroundColor = this.getHexColorFromBackendColor(this.signatureImagetextParams.backgroundColor)
          this.textBackgroundColorIsTransparent = false
        } else {
          this.textBackgroundColorIsTransparent = true
          this.signatureTextBackgroundColor = 'transparent'
        }

        // if (this.signatureImagetextParams.textToImageRatio) {
        if (this.signatureImagetextParams.size) {
          this.textToImageRatio = this.signatureImagetextParams.size
        } else {
          this.textToImageRatio = this.defaultTextToImageRatio
        }
      }
    },

    loadSignatureImageFromFile(event) {
      const file = event
      if (!(file.size > this.biggestSignatureImageSize)) {
        const reader = new FileReader()
        reader.onload = (event) => this.setSignatureImage(file, event.target.result)
        reader.readAsDataURL(file)
      } else {
        showToastError(this.$t('toastMessage.selectedFileIsTooLarge'))
      }
    },

    setSignatureImage(file, imageDataUri, callPreview = true) {
      let updatedDataUri = ''
      const splittedSigImgDataUrl = imageDataUri.split(';')
      updatedDataUri = updatedDataUri.concat(splittedSigImgDataUrl[0], ';filename=' + file.name + ';', splittedSigImgDataUrl[1])
      this.$set(this.signatureProfile, 'sourceImageDataUrl', updatedDataUri)
      if (callPreview) {
        this.initPreview()
      }
    },

    async listMyActivatedCredentials() {
      try {
        const resp = await rssp.listMyActivatedCredentials()
        resp.content = resp.content.filter(elem => ((elem.keyPurpose === 'ASYMMETRIC_SIGN') && (elem.certChain.length > 0)))

        this.activatedCredentials = resp.content
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    async listImportedCredentials() {
      try {
        const pageRequestImported = {
          'page-num': 1,
          'page-size': 50,
          'sort-field': 'user.name',
          'sort-order': 'desc'
        }

        const pageImported = await credentialRefs.listCredentialRefs(this.user.id, null, pageRequestImported)

        this.credentialRefs = pageImported.content

        let pageAlfaImported = { content: [], total: 0, pageNum: 1 }
        if (this.$store.state.remoteConfig.alfaEnabled) {
          pageAlfaImported = await alfaRefs.listAlfaRefs(
            this.isAdmin ? null : this.user.id,
            this.search,
            pageRequestImported
          )
          pageAlfaImported.content = (pageAlfaImported.content || []).map(item => ({ ...item, isAlfa: true }))
          this.credentialsAlfa = pageAlfaImported.content
        }
        if (this.evroEnabled) {
          const pageEvroImported = await evro.listEvroRefs(this.user.id, null, pageRequestImported)
          pageEvroImported.content = pageEvroImported.content.map(item => {
            return {
              ...item,
              isEvro: true
            }
          })
          this.credentialsEvro = pageEvroImported.content
        }
        this.haveImportedcontent = (pageImported.content.length > 0) || (this.credentialsEvro.length > 0)
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    backToParent(e) {
      if (!e) {
        this.$store.commit('removeCurrentFiles')
        this.$router.push('/signatureProfiles')
      }
    },
    handleDragDrop(e) {
      this.signatureImageUrl = e
      this.initPreview()
    },
    openSignatureModal() {
      this.$refs.signatureDrawer.dialog = true
      // this.$refs.signatureDrawer.showDrawingCanvas = true
    },
    handleCustomSignature(imageData) {
      const tempFile = {}
      tempFile.name = 'CustomSignature.png'

      this.setSignatureImage(tempFile, imageData, false)

      this.signatureImageUrl = this.signatureProfile.sourceImageDataUrl
      const splittedSigImgDataUrl = this.signatureProfile.sourceImageDataUrl.split(';')
      this.signatureImageFile = new File([splittedSigImgDataUrl[2].split(',')[1]], splittedSigImgDataUrl[1].split('=')[1],
        { type: splittedSigImgDataUrl[0].split(':')[1] })

      this.initPreview()
    },
    getCredentialItemText(item) {
      if (item.provider) {
        return `${item.description} | external: ${item.provider}`
      } else {
        if (item.isEvro) {
          return `${item.description} | evrotrust`
        } else
        if (item.isAlfa) {
          return `${item.description} | alfasign`
        } else {
          return item.description
        }
      }
    },
    loadGoogleFonts() {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href =
        'https://fonts.googleapis.com/css?family=' +
        this.allowedFonts.map((font) => font.family.replace(/\s+/g, '+')).join('|') +
        '&display=swap'
      document.head.appendChild(link)
    }
  }
}
</script>

<style>
.imagePreviewWrapper {
  width: 10%;
  outline: 1px dashed black;
}
.canvas {
  border: 1px solid black;
  direction: ltr;
}

#preview {
  border: 1px solid black;
  direction: ltr;
  width: 600px;
  height: 200px;
}

#signatureProfileModal .dragAndDropCard .v-card__subtitle,
.v-card__text,
.v-card__title {
  padding: 4px;
}
</style>
